/**
 * EmptyStatePanel - Ocean Alk-Align Project Overview Component
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Renders the comprehensive project overview and instructions
 * when no buoys are selected.
 * 
 * Responsibilities:
 * - Display Ocean Alk-Align project information
 * - Provide usage instructions for the dashboard
 * - Show feature highlights and context
 * - Render in an accessible, informative layout
 */

import React from 'react';
import styles from '../SensorDashboard.module.css';

export const EmptyStatePanel = React.memo(() => (
  <div className={styles.emptyCard}>
    <div style={{ 
      width: '100%', 
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '2rem',
      color: '#495057',
      lineHeight: '1.6'
    }}>
      <h1 style={{ 
        color: '#2c3e50', 
        textAlign: 'center', 
        marginBottom: '1.5rem',
        borderBottom: '3px solid #007bff',
        paddingBottom: '1rem',
        fontSize: '2.5rem'
      }}>
        Ocean Alk-Align Buoy Dashboard
      </h1>

      <div style={{
        background: '#e7f3ff',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        border: '1px solid #b3d9ff'
      }}>
        <h2 style={{ color: '#2c3e50', marginTop: '0', marginBottom: '1rem' }}>
          Real-time Monitoring for Ocean Alkalinity Enhancement Research
        </h2>
        <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          This dashboard provides real-time access to oceanographic sensor data from buoy deployments 
          supporting Ocean Alkalinity Enhancement (OAE) research. Monitor CO₂ levels, water chemistry, 
          and environmental parameters critical for understanding marine carbon dioxide removal.
        </p>
        <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>
          <strong>Get Started:</strong> Select a time range and buoy above to view live sensor data, 
          identify data quality issues, and export measurements for analysis.
        </p>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <a 
          href="https://alkalign.ocean.dal.ca/#:~:text=Ocean%20Alk-Align%20aims%20to%20provide%20a%20broad%20knowledge,removal%3B%20%282%29%20environmental%20safety%3B%20%283%29%20monitoring%20and%20verification"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: '#007bff',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: '500',
            transition: 'background-color 0.2s',
            fontSize: '1.1rem'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
        >
          Learn More About Ocean Alk-Align →
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        <div>
          <h3 style={{ color: '#007bff', marginBottom: '1rem' }}>The Ocean Alk-Align Project</h3>
          <p style={{ marginBottom: '1rem' }}>
            Ocean Alk-Align is a research consortium investigating Ocean Alkalinity Enhancement (OAE) 
            as a marine Carbon Dioxide Removal (CDR) technology. The project focuses on three critical aspects:
          </p>
          <ol style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>Efficiency & Permanence:</strong> CO₂ removal effectiveness and storage durability
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>Environmental Safety:</strong> Ecological impacts and risk assessment
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>Monitoring & Verification:</strong> MRV frameworks for deployment tracking
            </li>
          </ol>
          <p>
            The consortium is outcome-agnostic and does not advocate for any specific OAE method, 
            focusing instead on providing objective scientific evidence.
          </p>
        </div>

        <div>
          <h3 style={{ color: '#007bff', marginBottom: '1rem' }}>What is Ocean Alkalinity Enhancement?</h3>
          <p style={{ marginBottom: '1rem' }}>
            OAE mimics natural rock weathering by adding alkaline minerals to seawater. This process:
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>
              Neutralizes CO₂ by converting it to stable bicarbonate (HCO₃⁻)
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              Creates disequilibrium that draws more CO₂ from the atmosphere
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              Stores carbon in the ocean for extended periods
            </li>
            <li>
              Has potential for large-scale CO₂ removal with proper implementation
            </li>
          </ul>
          <p>
            Modeling studies suggest OAE has the largest CDR potential among the ~15 proposed 
            marine carbon removal technologies.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ color: '#2c3e50', marginTop: '0', marginBottom: '1rem' }}>
            How to Use This Dashboard
          </h3>
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>1. Select Time Range</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Choose from 1 hour to 30 days using the dropdown at the top left
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>2. Pick Your Buoy</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Click CT1002 or CT1003 buttons to load that buoy's available sensors
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>3. Choose Sensors & Fields</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Select sensors (ProCV, CTD, etc.) then specific measurements to display
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>4. Analyze Data</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Switch between Graph and Table views. Click-drag to zoom graphs
              </p>
            </div>
            <div>
              <strong style={{ color: '#dc3545' }}>5. Quality Control</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Bad data appears in red. Click red cells in Table view to exclude forever
              </p>
            </div>
          </div>
        </div>

        <div style={{
          background: '#e7f3ff',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #b3d9ff'
        }}>
          <h3 style={{ color: '#2c3e50', marginTop: '0', marginBottom: '1rem' }}>
            Dashboard Features
          </h3>
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>📊 Real-time Data</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Live pCO₂, temperature, salinity, and conductivity measurements
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>🔍 Interactive Charts</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Hover for tooltips, click-drag to zoom, reset button to return
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#007bff' }}>📋 Data Tables</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Sortable, scrollable tables with timestamp and measurement details
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#dc3545' }}>⚠️ Quality Control</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Automatic bad data detection with click-to-exclude functionality
              </p>
            </div>
            <div>
              <strong style={{ color: '#28a745' }}>💾 Data Export</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                Export filtered data as CSV for further analysis in your tools
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ 
        textAlign: 'center', 
        background: '#fff3cd',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid #ffc107'
      }}>
        <p style={{ margin: 0, fontSize: '1.1rem' }}>
          <strong>Why This Matters:</strong> Large-scale carbon dioxide removal is essential for meeting 
          Paris Agreement goals. 344 out of 400 IPCC scenarios rely on Negative Emission Technologies.
        </p>
      </div>
    </div>
  </div>
));

EmptyStatePanel.displayName = 'EmptyStatePanel';

export default EmptyStatePanel;